var _ = require('underscore');
var AccordionFormWidget = require('./../widget/accordion/AccordionFormWidget');
var patientSettingsPasswordAccordionFormTemplate = require('./patientSettingsPasswordAccordionFormTemplate.hbs');
var capslockDetectorUtils = require('./../utils/capslockDetectorUtils');
var PatientSettingsPasswordFormModel = require('./PatientSettingsPasswordFormModel');
var patientSettingsService = require('./patientSettingsService');

var MS_TO_SHOW_SAVE_SUCCESS = 10 * 1000;
/**
 * Form View for Patient Portal Settings Password Accordion
 *
 * @class PatientSettingsPasswordAccordionFormView
 * @extends AccordionFormWidget
 */
module.exports = AccordionFormWidget.extend({
  template: patientSettingsPasswordAccordionFormTemplate,

  events: _.extend({}, AccordionFormWidget.prototype.events, {
    'click [data-show-password]': '_togglePasswordDisplay',
    'click [data-save-password]': '_save'
  }),

  feedback: [{
   //******** Password *********
   /**
    * On invalid password event, show error message
    * @property feedback[0] (Password Error Message) {When/Then Feedback}
    */
    when: {
      '@newPassword' : ['keyup', 'blur', 'change']
    },
    then: function() {
      var isValid = this.model.isValid('newPassword');
      if (!isValid) {
        return {
          removeClass: 'brec-feedback--hidden',
          removeAttr: ['aria-hidden']
        };
      } else {
        return {
          addClass: 'brec-feedback--hidden',
          attr: ['aria-hidden', 'true']
        };
      }
    },
    to: 'passwordError'
  }, {
    /**
    * On invalid password event, show error message
    * @property feedback[1] (Password Input Field) {When/Then Feedback}
    */
    when: {
      '@newPassword' : ['keyup', 'blur', 'change']
    },
    then: function() {
      return this._thenAddClassIfInvalid('newPassword', 'brec-form__input--error');
    },
    to: 'newPassword'
  }, {
   //******** Confirm Password *********
   /**
    * On keyup or blur on the newPassword or confirmPassword or submits, validate the confirmPassword
    * @property feedback[2] (Confirm Password Error Message) {When/Then Feedback}
    */
   when: {
      '@newPassword' : ['keyup', 'blur', 'change'],
      '@confirmPassword' : ['keyup', 'blur', 'change']
    },
   then: function() {
      var isValid = this.model.isValid('confirmPassword');
      if (!isValid) {
        var errorText = this._thenFormatFeedback(this.model.preValidate('confirmPassword'), {
          'required.failed': 'patient.settings.password.confirmPassword.error.required',
          'error.match': 'patient.settings.password.password.error.match'
        });
        var result = {
          removeClass: 'brec-feedback--hidden',
          removeAttr: ['aria-hidden']
        };
        return _.extend(result, errorText);
      } else {
        return {
          addClass: 'brec-feedback--hidden',
          attr: ['aria-hidden', 'true']
        };
      }
    },
    to: 'confirmPasswordError'
  }, {
    /**
    * On keyup or blur on the newPassword or confirmPassword or submits, validate the confirmPassword
    * @property feedback[3] (Confirm Password Input Field) {When/Then Feedback}
    */
    when: {
      '@newPassword' : ['keyup', 'blur', 'change'],
      '@confirmPassword' : ['keyup', 'blur', 'change']
    },
    then: function() {
      return this._thenAddClassIfInvalid('confirmPassword', 'brec-form__input--error');
    },
    to: 'confirmPassword'
  }, {
   /**
    * On keypress on the newPassword, check if the Capslock is on
    * @property feedback[4] (New Password Capslock On Warning) {When/Then Feedback}
    */
    when: {
      '@newPassword' : ['keypress']
    },
    then: function(event) {
      if (!capslockDetectorUtils.canCapsLockBeDetected(event)) {
        return;
      }
      if (capslockDetectorUtils.isCapsLockOn(event)) {
        return {
          removeClass: 'brec-hidden',
          attr: ['aria-hidden', 'false']
        };
      } else {
        return {
          addClass: 'brec-hidden',
          attr: ['aria-hidden', 'true']
        };
      }
    },
    to: 'newPasswordCapslockOnWarning'
  }, {
   /**
    * On keypress on the confirmPassword, check if the Capslock is on
    * @property feedback[5] (Confirmed Password Capslock On Warning) {When/Then Feedback}
    */
    when: {
      '@confirmPassword' : ['keypress']
    },
    then: function(event) {
      if (!capslockDetectorUtils.canCapsLockBeDetected(event)) {
        return;
      }
      if (capslockDetectorUtils.isCapsLockOn(event)) {
        return {
          removeClass: 'brec-hidden',
          attr: ['aria-hidden', 'false']
        };
      } else {
        return {
          addClass: 'brec-hidden',
          attr: ['aria-hidden', 'true']
        };
      }
    },
    to: 'confirmPasswordCapslockOnWarning'
  }, {
       /**
    * On model change, if the model has validation errors, disable the save button.
    * @property feedback[6] (Save Button) {When/Then Feedback}
    */
    when: {
      listenTo: [
        {
          object: 'model',
          events: 'change'
        },
      ]
    },
    then: function() {
      var errors = this.model.validate();
      errors = _.keys(errors);
      var noErrors = errors.length === 0;
      if (noErrors) {
        return {
          removeClass: 'brec-btn--disabled',
          removeAttr: ['disabled']
        };
      } else {
        return {
          addClass: 'brec-btn--disabled',
          attr: ['disabled', 'disabled']
        };
      }
    },
    to: 'saveBtn'
  }, {
   /**
    * On submitting, disable the save button.
    * @property feedback[7] (Save Button) {When/Then Feedback}
    */
    when: {
      'on': ['submitting']
    },
    then: function() {
      return {
        addClass: 'brec-btn--disabled',
        attr: ['disabled', 'disabled']
      };
    },
    to: 'saveBtn'
  }],

  /**
   * @method initialize
   * @param options {Object}
   *   @param options.patientVecnaId {String} the vecna id of the patient to render the data for.
   * @override
   */
  initialize: function(options) {
    AccordionFormWidget.prototype.initialize.apply(this, arguments);
    _.bindAll(this, '_setSavedSuccess', '_hideSaveSuccess', '_processErrors');
    this.patientVecnaId = options.patientVecnaId;
    this.model = new PatientSettingsPasswordFormModel();
    this.set('showPassword', false);
    this.listenTo(this.viewState, 'change:showPassword change:submitting change:saved change:errors', this.render);
  },

  /**
   * Show/Hide password in the password input fields
   * @method _togglePasswordDisplay
   * @private
   */
  _togglePasswordDisplay: function(event) {
    event.preventDefault();
    var showPassword = this.get('showPassword');
    this.set('showPassword', !showPassword);
  },

  /**
   * Save new password
   * @method _save
   * @private
   */
  _save: function(event) {
    event.preventDefault();
    this.trigger('submitting');
    this.set('submitting', true);
    this.unset('errors');
    patientSettingsService.saveSettings(this.model, this.patientVecnaId)
      .then(this._setSavedSuccess, this._processErrors)
      .then(patientSettingsService.resetAndFetchModel, patientSettingsService.resetAndFetchModel);
  },

  /**
   * Triggers an event on portalUserModel that the new password is successfully saved
   * @method _setSavedSuccess
   * @private
   */
  _setSavedSuccess: function() {
    this.model = new PatientSettingsPasswordFormModel();
    this.set('saved', true);
    this.set('submitting', false);
    window.clearTimeout(this._hideSuccessTimeout);
    if (MS_TO_SHOW_SAVE_SUCCESS >= 0) {
      this._hideSuccessTimeout = window.setTimeout(this._hideSaveSuccess, MS_TO_SHOW_SAVE_SUCCESS);
    }
  },

  /**
   * Unsets that the save completed successfully on the vew state.
   * @method _hideSaveSuccess
   * @private
   */
  _hideSaveSuccess: function() {
    this.unset('saved');
  },

  /**
   * Sets the passed error onto the view state. The error code maps to i18n key as defined in the template.
   * If the response contains a message key for the error,
   * that will be used instead of the mapped response status message.
   *
   * @param response {Object} HTTP response
   * @private
   * @method _processErrors
   */
  _processErrors: function(response) {
    this.set('submitting', false);
    var responseStatus = response && response.status;
    var errors = this._errorCodeMap[responseStatus];
    if (_.isFunction(errors)) {
      var errorFn = _.bind(errors, this);
      errors = errorFn(response);
    }
    errors = errors || this._errorCodeMap.default;

    if (response && response.responseJSON && !_.isEmpty(response.responseJSON.specificReasons)) {
      var view = this;
      var invalidPasswordReasons = response.responseJSON.specificReasons.newPassword;
      var invalidPasswordReasonsKeys = _.map(invalidPasswordReasons, function(value){
        return view.format(value.messageKey, value.params);
      });
      this.set('invalidPasswordReasons', invalidPasswordReasonsKeys);
    }

    this.set('errors', errors);
  },

  /**
   * Map from error code to errors object to use.  If a function is defined then it will be
   * called in the context of this view.
   * @property _errorCodeMap
   * @type {Object}
   */
  _errorCodeMap: {
    500 : { errorCode: 'INTERNAL_ERROR' },
    default : { errorCode: 'ERROR' }
  }
});
